/* Cart Progress Bar - Atelier 3.0
   - Atualiza em tempo real via eventos + MutationObserver (sem monkeypatch agressivo)
   - Busca /cart.js com throttle, e reaproveita o mesmo payload para todas as instâncias
*/
(() => {
  const BUS_KEY = "__osCartProgressBus__";

  const throttle = (fn, wait = 250) => {
    let t = null;
    let lastArgs = null;
    return (...args) => {
      lastArgs = args;
      if (t) return;
      t = setTimeout(() => {
        t = null;
        fn(...lastArgs);
        lastArgs = null;
      }, wait);
    };
  };

  const formatMoney = (cents) => {
    // Preferir formatador nativo do tema (quando existe)
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        // Usa o money_format do Shopify, se disponível
        const fmt = (window.theme && window.theme.moneyFormat) || (window.Shopify.money_format) || "{{amount_with_currency}}";
        return window.Shopify.formatMoney(cents, fmt);
      }
    } catch (_) {}
    // Fallback: BRL (seu caso) / Intl
    try {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
    } catch (_) {
      return `R$ ${(cents / 100).toFixed(2).replace(".", ",")}`;
    }
  };

  const ensureBus = () => {
    if (window[BUS_KEY]) return window[BUS_KEY];

    const bus = {
      instances: new Set(),
      lastCart: null,
      fetching: null,
      cartUrl: null,
      refreshCart: async () => {
        if (!bus.cartUrl) return null;
        if (bus.fetching) return bus.fetching;

        bus.fetching = fetch(bus.cartUrl, { credentials: "same-origin" })
          .then((r) => (r.ok ? r.json() : null))
          .then((cart) => {
            bus.lastCart = cart;
            if (cart) bus.notify(cart);
            return cart;
          })
          .catch(() => null)
          .finally(() => {
            bus.fetching = null;
          });

        return bus.fetching;
      },
      notify: (cart) => {
        bus.instances.forEach((inst) => {
          try { inst.updateFromCart(cart); } catch (_) {}
        });
      },
      setupGlobalListenersOnce: (() => {
        let did = false;
        return () => {
          if (did) return;
          did = true;

          const requestRefresh = throttle(() => bus.refreshCart(), 200);

          // Eventos comuns em temas (Dawn-like e variações)
          document.addEventListener("cart:updated", (e) => {
            const cart = e?.detail?.cart || e?.detail;
            if (cart && typeof cart.total_price === "number") bus.notify(cart);
            else requestRefresh();
          });

          document.addEventListener("cart:change", requestRefresh);
          document.addEventListener("cart:refresh", requestRefresh);
          document.addEventListener("cart:rendered", requestRefresh);
          document.addEventListener("cart-drawer:open", requestRefresh);

          // Fallback robusto: se o drawer re-renderiza DOM, atualiza sem precisar “adivinhar” eventos
          const mo = new MutationObserver(requestRefresh);
          mo.observe(document.documentElement, { subtree: true, childList: true });
        };
      })(),
    };

    window[BUS_KEY] = bus;
    return bus;
  };

  class CartProgressBar extends HTMLElement {
    connectedCallback() {
      this._fill = this.querySelector("[data-cpb-fill]");
      this._text = this.querySelector("[data-cpb-text]");
      this._bar = this.querySelector(".cpb__bar");

      this._threshold = Number(this.dataset.thresholdCents || 0);
      this._msgPre = (this.dataset.msgPre || "").trim();
      this._msgPost = (this.dataset.msgPost || "").trim();
      this._cartUrl = this.dataset.cartJsonUrl;

      const bus = ensureBus();
      if (!bus.cartUrl && this._cartUrl) bus.cartUrl = this._cartUrl;

      bus.instances.add(this);
      bus.setupGlobalListenersOnce();

      // Primeiro paint: se já existe cart cacheado, usa; senão busca
      if (bus.lastCart) this.updateFromCart(bus.lastCart);
      else bus.refreshCart();
    }

    disconnectedCallback() {
      const bus = ensureBus();
      bus.instances.delete(this);
    }

    updateFromCart(cart) {
      if (!cart || typeof cart.total_price !== "number") return;
      if (!this._threshold || this._threshold <= 0) return;

      const total = cart.total_price;
      const remaining = Math.max(this._threshold - total, 0);
      const pct = Math.max(0, Math.min(100, Math.round((total / this._threshold) * 100)));

      const reached = remaining <= 0;

      const thresholdStr = formatMoney(this._threshold);
      const remainingStr = formatMoney(remaining);

      const msg = (reached ? this._msgPost : this._msgPre)
        .replaceAll("{{remaining}}", remainingStr)
        .replaceAll("{{threshold}}", thresholdStr);

      if (this._text) this._text.textContent = msg;

      if (this._fill) {
        this._fill.style.width = `${pct}%`;
        this._fill.style.background = reached
          ? getComputedStyle(this).getPropertyValue("--cpb-after").trim() || "currentColor"
          : getComputedStyle(this).getPropertyValue("--cpb-before").trim() || "currentColor";
      }

      if (this._bar) {
        this._bar.setAttribute("aria-valuenow", String(pct));
      }
    }
  }

  if (!customElements.get("cart-progress-bar")) {
    customElements.define("cart-progress-bar", CartProgressBar);
  }
})();
